import React, { useState, useMemo } from "react";
import { getColorClasses, getTextColorClass } from "../../utils/getBackgroundColor";
import { AccordionBlock as AccordionBlockType, OptimizelyLongString, OptimizelyXhtmlString } from "../../types/optimizely/blocks";
import classNames from "classnames";
import dompurify from "dompurify";
import { ColorPalette } from "../../types/background";
import { renderToString } from "react-dom/server";
import logo from "../../assets/wilhelmsen_logo_flag.svg";
import logoWhite from "../../assets/wilhelmsen_logo_flag_white.svg";
import ArrowRight from "../../assets/arrow-right.svg?react";
import { OptimizelyExpandedValue } from "../../types/optimizely/expanded";
import { AccordionItem } from './AccordionItem/AccordionItem';

interface AccordionItem extends OptimizelyExpandedValue {
  sectionDescription: { value: string };
}

interface AccordionBlockProps extends AccordionBlockType {
  sections?: {
    expandedValue: AccordionItem;
  }
  backgroundColor?: { value: string };
  additionalColor?: { value: string };
  header?: { value: string };
  preHeading?: { value: string };
  overflowingContent?: { value: string };
  text?: OptimizelyXhtmlString;
  sectionId?: OptimizelyLongString;
}

const getAnchorClasses = (backgroundColor: string) =>
  `w-1/2 mt-7 py-3 bg-transparent ${backgroundColor} hover:${backgroundColor} items-center font-bold rounded flex`;
const localPurifier = dompurify();

const AccordionBlock: React.FC<AccordionBlockProps> = ({
  sections,
  backgroundColor: optiBgColor,
  additionalColor: optiAdditionalColor,
  header,
  preHeading,
  overflowingContent,
  text,
  name,
  sectionId
}) => {
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const backgroundColor = getColorClasses(optiBgColor?.value as ColorPalette);
  const textClass = getTextColorClass(optiBgColor?.value as ColorPalette);

  const additionalColor = getColorClasses(optiAdditionalColor?.value as ColorPalette);
  const additionalTextColor = getTextColorClass(optiAdditionalColor?.value as ColorPalette);

  const toggleAccordion = (index: number) => {
    setOpenIndexes(prevIndexes => 
      prevIndexes.includes(index)
        ? prevIndexes.filter(i => i !== index)
        : [...prevIndexes, index]
    );
  };

  const transformedSections = useMemo(() => 
    sections?.expandedValue && Array.isArray(sections.expandedValue)
      ? sections.expandedValue.map(section => ({
          title: { value: section.sectionHeader.value },
          sectionDescription: { value: section.sectionDescription.value },
          content: { value: section.sectionContent.expandedValue[0]?.text.value || '' }
        }))
      : [],
    [sections]
  );

  localPurifier.addHook("beforeSanitizeAttributes", (node) => {
    if (node.nodeName && node.nodeName === "A") {
      const hasSvgChild =
        Array.from(node.childNodes).filter((child) => child.nodeName === "SPAN")
          .length > 0;
      node.setAttribute(
        "class",
        `${getAnchorClasses(
          backgroundColor
        )} font-normal group hover:text-primaryBlue`
      );

      if (!hasSvgChild) {
        const icon = renderToString(
          <span className="ml-2 group-hover:translate-x-2 transition-all ease-out">
            <ArrowRight />
          </span>
        );
        node.innerHTML += icon;
      }
    }
  });

  return (
    <div className="flex flex-row items-center w-full relative" role="region" aria-label={'Accordion section'} id={sectionId?.value ?? ''}>
      {text?.value ? <div className={classNames("p-12 lg:p-[60px] flex-col justify-between whitespace-pre-wrap h-full w-1/2 hidden md:flex min-h-[558px]", additionalColor)}>
      <img src={additionalTextColor === 'text-white' ? logoWhite : logo} alt="Wilhelmsen logo" className="object-cover" width="84" height="84" />
      <div>
        <h2 className={classNames("text-[42px]", additionalTextColor)}>{preHeading?.value}</h2>
        <h2 className={classNames("font-bold text-[42px] pb-4", additionalTextColor)}>{header?.value}</h2>
        <section
              className={classNames("text-gray-600 md:text-md w-full", additionalTextColor)}
              dangerouslySetInnerHTML={{
                __html: localPurifier.sanitize(text.value),
              }}
            />
      </div>
      </div> : null}
      <div className={classNames("flex flex-col py-12 px-6 md:p-[60px] md:pr-[129px]", backgroundColor, overflowingContent?.value ? 'h-auto min-h-full z-[5]' : '', text?.value ? 'w-full md:w-1/2 relative md:absolute right-0 top-0 ' : 'w-full')}>
        { text?.value ? <img src={textClass === 'text-white' ? logoWhite : logo} alt="Wilhelmsen logo" className="object-cover block md:hidden" width="84" height="84" /> : null}
        { name && text?.value ? <h3 className={classNames("w-full block md:hidden text-2xl mt-10", textClass)}>{name}:</h3> : null}
        { header?.value && text?.value ? <h3 className={classNames("w-full block md:hidden text-2xl mb-4 font-bold", textClass)}>{header?.value}</h3> : null}
        { header?.value && !text?.value ? <h3 className={classNames("w-full max-w-3xl mx-auto text-2xl font-bold mb-8", textClass)}>{header?.value}</h3> : null}
      <div className={classNames("w-full max-w-3xl mx-auto", header?.value && !text?.value ? 'border-t border-primary-200' : '')}>
        {transformedSections.map((item, index) => (
          <AccordionItem
            key={index}
            index={index}
            item={item}
            isOpen={openIndexes.includes(index)}
            textClass={textClass}
            hasText={!!text?.value}
            onToggle={toggleAccordion}
            openIndexes={openIndexes}
          />
        ))}
        {text?.value && openIndexes.length > 0 ? (
          <div className="flex justify-end">
            <button
              className={classNames(`text-md px-6 py-2 mt-3 border-2 rounded hidden md:block bg-blue-700`, textClass , textClass === 'text-white' ? 'border-white hover:border-white hover:text-white' : 'border-primary hover:border-primary hover:text-primary')}
              onClick={() => setOpenIndexes([])}
              aria-label="Close all accordion sections"
            >
              Close <span className="ml-2" aria-hidden="true">✕</span>
            </button>
          </div>
        ) : null}
        </div>
      </div>
    </div>
  );
};

export default AccordionBlock;